import { Box, Button, Card, CardContent, MenuItem, Select, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX } from "react";

import { modelDisplayName } from "@/lib/modelLabel";

import type { ProviderInfo } from "../types.js";

/**
 * One provider card — label, the provider's DEFAULT model (a picker over
 * every chat model the vendor offers this account), key-health badge,
 * capability tag, and a "Set as active" button (disabled when no key is
 * configured). Extracted from `ProvidersPanel.tsx`.
 *
 * The model list is the gateway's live one (`model_catalog`), so a new
 * release is pickable the day the vendor ships it. A model with no published
 * price is marked: the gateway meters it at the vendor's ceiling rate.
 */
export function ProviderCard({
  provider,
  onSetActive,
  onSetModel,
  modelBusy = false,
}: {
  provider: ProviderInfo;
  onSetActive: () => void;
  /** Pick the default model; `null` = back to the gateway's boot default. */
  onSetModel?: (model: string | null) => void;
  modelBusy?: boolean;
}): JSX.Element {
  const models = provider.models ?? [];
  return (
    <Card data-testid={`provider-card-${provider.id}`}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: "text.primary" }}>{provider.label}</Typography>
            {(models.length === 0 || !onSetModel) && (
              <Typography sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>{provider.model}</Typography>
            )}
          </Box>
          {provider.active && (
            <Box
              sx={{
                borderRadius: 0.5,
                px: 1,
                py: 0.25,
                fontSize: 12,
                color: "success.main",
                bgcolor: (t) => alpha(t.palette.success.main, 0.1),
              }}
            >
              Active
            </Box>
          )}
        </Box>

        {models.length > 0 && onSetModel && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
              Default model{" "}
              <Box component="span" sx={{ color: "text.disabled" }}>
                ({models.length} available)
              </Box>
            </Typography>
            <Select
              size="small"
              fullWidth
              value={models.some((m) => m.id === provider.model) ? provider.model : ""}
              displayEmpty
              disabled={modelBusy}
              inputProps={{ "aria-label": `${provider.label} default model` }}
              onChange={(e) => {
                const next = String(e.target.value);
                if (next && next !== provider.model) onSetModel(next);
              }}
              data-testid={`provider-model-${provider.id}`}
            >
              {!models.some((m) => m.id === provider.model) && (
                <MenuItem value="" disabled>
                  {modelDisplayName(provider.model)} (not offered any more)
                </MenuItem>
              )}
              {models.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, width: "100%" }}>
                    <span>{modelDisplayName(m.id)}</span>
                    <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "text.disabled" }}>
                      {m.id}
                      {!m.priced && " · no published price"}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1, fontSize: 12, color: "text.disabled" }}>
              {provider.model_source === "dashboard" ? (
                <>
                  Picked here.
                  <Button size="small" onClick={() => onSetModel(null)} disabled={modelBusy} sx={{ minWidth: 0, p: 0, fontSize: 12 }}>
                    Use the boot default
                  </Button>
                </>
              ) : (
                "Boot default from the gateway's settings."
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, fontSize: 12 }}>
          <KeyStatusBadge status={provider.key_status} />
          <Box component="span" sx={{ borderRadius: 0.5, bgcolor: "surfaceSoft", px: 0.75, py: 0.25, fontSize: 12, color: "text.disabled" }}>
            {provider.streaming ? "streaming + completions" : "completions only"}
          </Box>
        </Box>

        {!provider.active && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={onSetActive}
              disabled={provider.key_status === "missing"}
              {...(provider.key_status === "missing"
                ? { title: "No API key configured for this provider on the gateway." }
                : {})}
            >
              Set as active
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function KeyStatusBadge({ status }: { status: string }): JSX.Element {
  const tone =
    status === "ok"
      ? { color: "success.main" as const, alphaMain: "success" as const }
      : status === "invalid" || status === "missing"
        ? { color: "error.main" as const, alphaMain: "error" as const }
        : { color: "warning.main" as const, alphaMain: "warning" as const };
  return (
    <Box
      component="span"
      sx={{
        borderRadius: 0.5,
        px: 0.75,
        py: 0.25,
        fontSize: 12,
        color: tone.color,
        bgcolor: (t) => alpha(t.palette[tone.alphaMain].main, 0.1),
      }}
    >
      key: {status}
    </Box>
  );
}
